import {inject, Injectable} from "@angular/core";
import {LargeStorageLocal} from "@app/core/shared/domain/storage/large-storage.local";
import {CartLineDb} from '@app/core/cart/infrastructure/local/cart-line-db';

@Injectable({
    providedIn: 'root'
})
export class CartLineDAO {

    // The key to store the cart lines in the local storage
    private LOCAL_STORAGE_KEY = 'cart-lines';

    private localStorage = inject(LargeStorageLocal);

    /**
     * Find the stored cart line with the given product code and promotion code.
     */
    find(productCode: string, promotionCode: string): Promise<CartLineDb | null> {
        const cartLines = this.getCartLines();
        const foundCartLine = cartLines.find((cartLine: CartLineDb) => cartLine.code === productCode && cartLine.promotionCode === promotionCode) ?? null;
        return Promise.resolve(foundCartLine);
    }

    /**
     * Get all the stored cart lines.
     */
    findAll(): Promise<CartLineDb[]> {
        const cartLines = this.getCartLines();
        return Promise.resolve(cartLines);
    }

    /**
     * Get all the stored cart lines that have the given promotion code.
     */
    findByPromotionCode(promotionCode: string): Promise<CartLineDb[]> {
        const cartLines = this.getCartLines();
        return Promise.resolve(cartLines.filter((cartLine: CartLineDb) => cartLine.promotionCode === promotionCode));
    }

    /**
     * Remove the given cart line from the storage.
     */
    remove(cartLineDB: CartLineDb): Promise<CartLineDb> {
        const cartLines = this.getCartLines();
        const newCartLines = cartLines.filter((cartLine: CartLineDb) => cartLine.code !== cartLineDB.code || cartLine.promotionCode !== cartLineDB.promotionCode);
        this.updateLocalStorage(newCartLines);
        return Promise.resolve(cartLineDB);
    }


    /**
     * Remove all the given cart lines from the storage.
     */
    removeAll(cartLineDBs: CartLineDb[]): Promise<CartLineDb[]> {
        const cartLines = this.getCartLines();
        const newCartLines = cartLines.filter((cartLine: CartLineDb) => !cartLineDBs.find((cartLineDB: CartLineDb) => cartLineDB.code === cartLine.code && cartLineDB.promotionCode === cartLine.promotionCode));
        this.updateLocalStorage(newCartLines);
        return Promise.resolve(cartLineDBs);
    }

    /**
     * Save the given cart line in the storage.
     */
    save(cartLineDB: CartLineDb): Promise<CartLineDb> {
        const cartLines = this.getCartLines();
        const newCartLines = [...cartLines, cartLineDB];
        this.updateLocalStorage(newCartLines);
        return Promise.resolve(cartLineDB);
    }

    /**
     * Update the given stored cart line.
     */
    update(cartLineDB: CartLineDb): Promise<CartLineDb> {
        const cartLines = this.getCartLines();
        const newCartLines = cartLines.map((cartLine: CartLineDb) => cartLine.code === cartLineDB.code && cartLine.promotionCode === cartLineDB.promotionCode ? cartLineDB : cartLine);
        this.updateLocalStorage(newCartLines);
        return Promise.resolve(cartLineDB);
    }
    
    /**
     * Remove all the stored cart lines.
     */
    clear(): Promise<void> {
        this.updateLocalStorage([]);
        return Promise.resolve();
    }
   
    private updateLocalStorage(cartLines: CartLineDb[]) {
        this.localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(cartLines));
    }

    private getCartLines(): CartLineDb[] {
        const cartLines = JSON.parse( this.localStorage.getItem(this.LOCAL_STORAGE_KEY) ?? '[]')
        return cartLines.map((cartLine: CartLineDb) => ({
            ...cartLine,
            // Ensure that the values are numbers
            price: Number(cartLine.price),
            unitPrice: Number(cartLine.unitPrice),
            palletPrice: Number(cartLine.palletPrice),
        }));
    }

}
